// src/App.js
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import SimpleReactLightbox from 'simple-react-lightbox';
import store from './redux/store';
import Admin from './routes/admin';
import Borrower from './routes/borrower'; // Keep this import for the component
import Investor from './routes/investor';
import Facilitator from './routes/facilitator';
import Auth from './routes/auth';
import LoadingComponent from './components/LoadingComponent';

import './static/css/style.css';
import config from './config/config';
import ProtectedRoute from './components/utilities/protectedRoute';
import 'antd/dist/antd.less';
import ResizeObserverErrorHandler from './components/ResizeObserverErrorHandler';

const NotFound = lazy(() => import('./container/pages/404'));
const LandingLayout = lazy(() => import('./container/landing/LandingLayout'));
const HomePage = lazy(() => import('./container/landing/HomePage'));
const AboutPage = lazy(() => import('./container/landing/AboutPage'));
const ContactPage = lazy(() => import('./container/landing/ContactPage'));
const FAQPage = lazy(() => import('./container/landing/FAQPage'));

const { themeColor } = config;

function ProviderConfig() {
  const { rtl, isLoggedIn, topMenu, mainContent, userType } = useSelector(function (state) {
    return {
      rtl: state.ChangeLayoutMode.rtlData,
      topMenu: state.ChangeLayoutMode.topMenu,
      mainContent: state.ChangeLayoutMode.mode,
      isLoggedIn: state.auth.login,
      userType: state.auth.userType,
    };
  });

  const [, setPath] = useState(window.location.pathname);

  useEffect(
    function () {
      let unmounted = false;
      if (!unmounted) {
        setPath(window.location.pathname);
      }
      return function () {
        unmounted = true;
      };
    },
    [setPath],
  );

  function getDefaultRoute() {
    switch (userType) {
      case 'admin':
        return '/admin';
      case 'borrower':
        return '/manager'; // *** MODIFIED: Borrower's default route is now /manager
      case 'investor':
        return '/investor';
      case 'facilitator':
        return '/facilitator';
      default:
        // This default might also need consideration based on your app's logic
        // For example, if userType is null, redirect to login or a public dashboard
        return '/admin'; // Fallback, consider if this is appropriate for unknown userType
    }
  }

  function getUserComponent(type) {
    switch (type) {
      case 'admin':
        return Admin;
      case 'borrower':
        return Borrower; // Still return the Borrower component
      case 'investor':
        return Investor;
      case 'facilitator':
        return Facilitator;
      default:
        return Admin;
    }
  }
  const observerErrorHandler = (e) => {
    if (
      e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded'
    ) {
      // Prevent the error from bubbling
      e.stopImmediatePropagation();
    }
  };

  window.addEventListener('error', observerErrorHandler);
  window.addEventListener('unhandledrejection', (e) => {
    // Optional: handle promise-related ResizeObserver issues
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
      e.preventDefault();
    }
  });

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'}>
      <ThemeProvider theme={{ ...themeColor, rtl, topMenu, mainContent }}>
        <SimpleReactLightbox
          options={{
            settings: {
              disablePanzoom: true, // Disable features that might cause resize issues
              disableWheelControls: true,
            },
          }}
        >
          <Router basename={process.env.PUBLIC_URL}>
            <Suspense fallback={<LoadingComponent />}>
              <Routes>
                {/* Public landing page routes */}
                <Route path="/" element={<LandingLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="faq" element={<FAQPage />} />
                </Route>

                {/* Auth routes */}
                <Route path="/auth" element={<LandingLayout />}>
                  <Route index element={<Auth />} />
                  <Route path="*" element={<Auth />} />
                </Route>

                {/* Protected routes - only accessible if logged in */}
                {isLoggedIn ? (
                  <>
                    <Route
                      path="/admin/*"
                      element={<ProtectedRoute Component={getUserComponent('admin')} allowedRoles={['admin']} />}
                    />
                    {/* *** NEW ROUTE FOR BORROWER AT /manager *** */}
                    <Route
                      path="/manager/*" // Borrower's main protected path
                      element={
                        <ProtectedRoute Component={getUserComponent('borrower')} allowedRoles={['borrower', 'admin']} />
                      }
                    />
                    {/* Optional: If you want to explicitly redirect old /borrower/* paths */}
                    <Route path="/borrower/*" element={<Navigate to="/manager" replace />} />

                    <Route
                      path="/investor/*"
                      element={
                        <ProtectedRoute Component={getUserComponent('investor')} allowedRoles={['investor', 'admin']} />
                      }
                    />
                    <Route
                      path="/facilitator/*"
                      element={
                        <ProtectedRoute
                          Component={getUserComponent('facilitator')}
                          allowedRoles={['facilitator', 'admin']}
                        />
                      }
                    />
                    <Route path="/dashboard" element={<Navigate to={getDefaultRoute()} replace />} />
                    {/* Add a root redirect if the user is logged in but lands on a non-specific route */}
                    <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
                  </>
                ) : (
                  // If not logged in, redirect any protected path attempts to /auth
                  <Route path="/*" element={<Navigate to="/auth" />} />
                )}

                {/* Fallback for any unmatched paths */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Router>
        </SimpleReactLightbox>
      </ThemeProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      {/* You had ResizeObserverErrorHandler here, moved it outside ProviderConfig if it's a global error boundary */}
      {/* If ResizeObserverErrorHandler is meant to wrap the entire app, this is fine */}
      {/* If it's a component that needs theme/router context, keep it inside ProviderConfig */}
      {/* For now, leaving it outside as it was */}
      <ResizeObserverErrorHandler />
      <ProviderConfig />
    </Provider>
  );
}

export default App;
